import { useRef, useState } from "react";
import { CreditCard, Plus } from "lucide-react";
import { useClient } from "@/features/clients/hooks/clientQueries";
import { usePaymentMethods } from "@/features/payment-methods/hooks/paymentMethodQueries";
import type { PaymentMethod } from "@/features/payment-methods/types/paymentMethodTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Checkbox } from "@/shared/components/shadcn/Checkbox";
import { AmountInput } from "@/shared/components/ui/AmountInput";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { AppointmentChargeGuard } from "../components/AppointmentChargeGuard";
import { usePaymentMutations } from "../hooks/paymentQueries";
import {
  formatPaymentMoney, parsePaymentAmount, paymentAmountInput, paymentCurrencyDigits,
  type CreatePaymentRequest, type PaymentSummary,
} from "../types/paymentTypes";

function ChargeForm({ appointmentId, clientId, businessId, userId, revision, summary }: {
  appointmentId: string; clientId: string; businessId: string; userId: string; revision: number; summary: PaymentSummary;
}) {
  const cards = usePaymentMethods(businessId, clientId, userId);
  const client = useClient(clientId);
  const { create } = usePaymentMutations(appointmentId);
  const capture = useWorkspaceOperation();
  const { goToAppointmentPayment } = useAppNavigation();
  const [amount, setAmount] = useState(() => paymentAmountInput(summary.balanceMinor ?? 0, summary.currency));
  const [selected, setSelected] = useState<PaymentMethod | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const attempt = useRef<CreatePaymentRequest | null>(null);
  const submitting = useRef(false);
  const amountMinor = parsePaymentAmount(amount, summary.currency);
  const amountError = amountMinor === null ? `Enter a positive amount${summary.currency === "JPY" ? " with no decimal places" : " with up to 2 decimal places"}.` :
    amountMinor > (summary.balanceMinor ?? 0) ? "The amount cannot exceed the amount due." : undefined;
  const busy = create.isPending;
  const locked = busy || !!attempt.current;
  const now = new Date();
  const cardsMatchEnvironment = !cards.data || cards.data.environment === summary.environment;
  const customerName = client.data ? [client.data.firstName, client.data.lastName].filter(Boolean).join(" ") : "this customer";
  function submit(card?: PaymentMethod) {
    if (submitting.current || amountError || !amountMinor || (card && !acknowledged)) return;
    attempt.current ??= {
      amountMinor, expectedRevision: revision, expectedCurrency: summary.currency, idempotencyKey: crypto.randomUUID(),
      method: card ? "saved_card" : "new_card",
      ...(card ? { paymentMethodId: card.id, acknowledged: true } : {}),
    };
    submitting.current = true;
    const isCurrent = capture();
    create.mutate(attempt.current, {
      onSuccess: payment => { if (isCurrent()) goToAppointmentPayment(appointmentId, payment.id); },
      onSettled: () => { submitting.current = false; },
    });
  }
  return <div className="space-y-4">
    {client.error ? <ErrorState message={client.error.message} onRetry={() => void client.refetch()} /> :
      <p className="font-medium">{customerName}</p>}
    <p className="text-sm text-muted-foreground">Amount due {formatPaymentMoney(summary.balanceMinor ?? 0, summary.currency)}.</p>
    <fieldset disabled={locked} className="min-w-0">
      <AmountInput label={`Amount to charge (${summary.currency})`} value={amount} onValueChange={setAmount} error={amountError} />
    </fieldset>
    <fieldset disabled={locked || !!amountError || !!client.error || client.isPending} className="min-w-0 space-y-4 disabled:opacity-60">
      <legend className="sr-only">Payment options</legend>
      <InfoRow title="New charge" subtitle="Let the customer pay on this device or with a secure link."
        icon={<Plus aria-hidden="true" />} onClick={() => submit()} className="rounded-[20px]" />
      {cards.isPending ? <RowSkeleton /> : cards.error || !cardsMatchEnvironment ? null :
        cards.data.cards.length === 0 ? <p className="text-sm text-muted-foreground">No saved cards.</p> :
          cards.data.cards.map(card => {
            const expired = card.expYear < now.getFullYear() || (card.expYear === now.getFullYear() && card.expMonth < now.getMonth() + 1);
            return <fieldset key={card.id} disabled={expired} className="min-w-0 disabled:opacity-60">
              <InfoRow title={`${card.brand} ending in ${card.last4}`}
                subtitle={`${expired ? "Expired" : "Expires"} ${String(card.expMonth).padStart(2, "0")}/${card.expYear}`}
                icon={<CreditCard aria-hidden="true" />} className="rounded-[20px]"
                onClick={() => { setAcknowledged(false); setSelected(card); }} />
            </fieldset>;
          })}
    </fieldset>
    {cards.error && <ErrorState message={cards.error.message} onRetry={() => void cards.refetch()} />}
    {!cardsMatchEnvironment && <ErrorState message="Saved cards do not match this payment's Stripe environment. Refresh cards before using one."
      onRetry={() => void cards.refetch()} />}
    {create.error && <div className="space-y-2">
      <ErrorState message={create.error.message} />
      <ErrorState message="The payment request could not be resolved. It may have reached Stripe. Retry this same request; do not start a replacement." />
      <Button disabled={busy} onClick={() => submit(selected ?? undefined)}>Retry this request</Button>
    </div>}
    <ConfirmationDialog open={!!selected} onOpenChange={open => { if (!open && !busy) setSelected(null); }}
      title="Charge saved card?" confirmLabel={`Charge ${formatPaymentMoney(amountMinor ?? 0, summary.currency)}`}
      dismissLabel="Back" pending={busy} confirmDisabled={!acknowledged || !!amountError}
      error={create.error ? "The result is not confirmed. Retry the same request or check the appointment history." : undefined}
      description={<>
        Charge {customerName} {formatPaymentMoney(amountMinor ?? 0, summary.currency)} using {selected?.brand} ending in {selected?.last4}.
        <label className="mt-3 flex items-start gap-2">
          <Checkbox checked={acknowledged} disabled={locked} onCheckedChange={value => setAcknowledged(value === true)} />
          <span>I confirm the customer agreed to this amount and timing for this appointment.</span>
        </label>
      </>}
      onConfirm={() => { if (selected) submit(selected); }} />
  </div>;
}

export function AppointmentCardChargePage() {
  return <AppointmentChargeGuard title="Card details">
    {({ appointmentId, appointment, summary, businessId, userId }) =>
      !summary.canCharge || !appointment.clientId || paymentCurrencyDigits(summary.currency) === null ?
        <ErrorState message={summary.unavailableReason ?? "This appointment cannot be charged. Check its customer, price, and currency."} /> :
        <ChargeForm key={`${userId}:${businessId}:${appointmentId}:${summary.currency}:${appointment.revision}`} appointmentId={appointmentId}
          clientId={appointment.clientId} businessId={businessId} userId={userId}
          revision={appointment.revision} summary={summary} />}
  </AppointmentChargeGuard>;
}
